import React, {useState, useEffect} from "react";
import {
  Card,
  makeStyles,
  CardHeader,
  CardContent,
  CardActions,
  IconButton,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  Divider,
  Button,
  Grid
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CancelIcon from "@material-ui/icons/Cancel";
import InfoIcon from "@material-ui/icons/Info";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import {compose} from "../../utils/compose";
import {connect} from "react-redux";
import {subscriptionActions} from "../../store/actions/subscription/subscription";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import AddCouponDialog from "./AddCouponDialog";
import CouponDescription from "./CouponDescription";
import {subscriptionBaseUrl, httpClient} from "../../http-client/http-client";
import {notificationActions} from "../../store/actions/notifications/notifications";
import ReactivateEstimateDialog from "./ReactivateEstimateDialog";
import PriceDetails from "./PriceDetails";
import ChangePlanDialog from "./ChangePlanDialog";
import {invoicesActions} from "../../store/actions/invoices/invoices";
import {userIsInAnyRole} from "../../auth/userManager";

const useStyles = makeStyles(theme => ({
  card: {},
  actionMore: {
    marginLeft: "auto"
  },
  cardTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  cardPrice: {
    fontSize: "1.6rem",
    fontWeight: "600",
    marginTop: "8px"
  },
  cardActions: {
    padding: 11
  },
  cardSelectedSection: {
    color: theme.palette.primary.main
  },
  textRight: {
    textAlign: "right"
  },
  cardPriceDivider: {
    backgroundColor: "rgba(255, 255, 255, 0.8)"
  }
}));

const ITEM_HEIGHT = 48;

function getLetterFromStatus(subStatus) {
  switch (subStatus) {
    case "Future":
      return "F";
    case "InTrial":
      return "T";
    case "Active":
      return "A";
    case "NonRenewing":
      return "N";
    case "Paused":
      return "P";
    case "Cancelled":
      return "C";
    case "Unknown":
    default:
      return "U";
  }
}

function TabPanel(props) {
  const {children, value, index, ...other} = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-prevent-tabpanel-${index}`}
      aria-labelledby={`scrollable-prevent-tab-${index}`}
      {...other}
    >
      {children}
    </Typography>
  );
}

const SubscriptionStatus = props => {
  const classes = useStyles();
  const {
    subscription,
    onFetchSubInfo,
    access_token,
    onShowNotification,
    hasPaymentMethod,
    onFetchInvoices,
    user
  } = props;

  const [subMoreAnchorEl, setSubMoreAnchorEl] = useState(null);
  const [subCancelConfirmOpen, setSubCancelConfirmOpen] = useState(false);
  const {t} = useTranslation();
  const open = Boolean(subMoreAnchorEl);
  const [acdOpen, setAcdOpen] = useState(false);
  const [redOpen, setRedOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [section, setSection] = React.useState(-1);

  useEffect(() => {
    onFetchSubInfo();
  }, [onFetchSubInfo]);

  function subMoreClickHandler(event) {
    setSubMoreAnchorEl(event.currentTarget);
  }

  function subMoreCloseHandler() {
    setSubMoreAnchorEl(null);
  }

  function cancelSubscriptionHandler(event) {
    console.log("Cancel subscription");
    setSubMoreAnchorEl(null);
    setSubCancelConfirmOpen(true);
  }

  async function subCancelConfirmHandler(value) {
    if (value === true) {
      console.log("Send cancel to server");
      setSubCancelConfirmOpen(false);

      const url = `${subscriptionBaseUrl}/cancel`;
      const model = {
        Reason: "Test reason"
      };
      try {
        const resp = await httpClient.post(url, access_token, model);

        onFetchSubInfo();
        const notification = {
          message: t("subscriptionCancelledNotification"),
          options: {
            variant: "success"
          }
        };
        onShowNotification(notification);
      } catch (error) {
        console.log(error.error.HumanError);
        const notification = {
          message: t(error.error.HumanError),
          options: {
            variant: "warning"
          },
          hasCloseButton: true
        };
        onShowNotification(notification);
      }
    } else {
      setSubCancelConfirmOpen(false);
    }
  }

  function handleChange(event, newValue) {
    if (section === newValue) {
      setSection(-1);
    } else {
      setSection(newValue);
    }
  }

  function openCouponDialogHandler(event) {
    setAcdOpen(true);
  }

  function acdCloseHandler(changed) {
    if (changed) {
      onFetchSubInfo();
    }
    setAcdOpen(false);
  }

  function reactivateSubscriptionHandler() {
    console.log("reactivateSubscription");
    setRedOpen(true);
  }

  function redCloseHandler(shouldRefresh) {
    if (shouldRefresh) {
      onFetchSubInfo();
    }
    setRedOpen(false);
  }

  function changePlanHandler(event) {
    console.log("changePlanHandler");
    setUpdateOpen(true);
  }

  function updateCloseHandler(shouldRefresh) {
    if (shouldRefresh) {
      onFetchSubInfo();
      onFetchInvoices();
    }
    setUpdateOpen(false);
  }

  function getSubheader(info, estimate) {
    switch (info.Status) {
      case "Future":
        return `${t("nextBillingDate")}: ${new Date(
          estimate.SubscriptionEstimate.NextBillingAt
        ).toDateString()}`;
      case "InTrial":
        return `${t("trialEnds")}: ${new Date(info.TrialEnd).toDateString()}`;
      case "Active":
        return `${t("nextBillingDate")}: ${new Date(
          estimate.SubscriptionEstimate.NextBillingAt
        ).toDateString()}`;
      case "NonRenewing":
        return `${t("subscriptionEnds")}: ${new Date(
          info.CurrentTermEnd
        ).toDateString()}`;
      case "Paused":
        return "P";
      case "Cancelled":
        return `${t("cancelledOn")}: ${new Date(
          info.CancelledAt
        ).toDateString()}`;
      case "Unknown":
      default:
        return "";
    }
  }

  function isCancellable(info) {
    switch (info.Status) {
      case "Future":
      case "InTrial":
      case "Active":
        return true;
      case "NonRenewing":
      case "Paused":
      case "Cancelled":
      case "Unknown":
      default:
        return false;
    }
  }

  function canGoToDashboard(info) {
    // Owners can always go to the dashboard even with inactive subscriptions
    if(userIsInAnyRole(user, ["Owner"])) {
      return true;
    }

    // All other roles require a plan of some sort
    switch(info.Status) {
      case "Future":
      case "InTrial":
      case "Active":
        return true;
      case "NonRenewing":
      case "Paused":
      case "Cancelled":
      case "Unknown":
      default:
        return false;
    }
  }

  function goToDashboard() {
    if(userIsInAnyRole(user, ["Groomer"])) {
      window.location.href = process.env.REACT_APP_PORTALV2_ENDPOINT;
    }
    else {
      window.location.href = process.env.REACT_APP_PORTAL_ENDPOINT;
    }
  }

  function canChangePlan(info) {
    if (info.Status === "InTrial") {
      return true;
    } else {
      return hasPaymentMethod;
    }
  }

  const features =
    subscription.data.Plan !== null
      ? JSON.parse(subscription.data.Plan.Features)
      : [];

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          title={t("subscriptionStatus")}
          className={classes.cardTitle}
        />
        {subscription.fetching ? (
          <CardContent>
            <CircularProgress/>
          </CardContent>
        ) : (
          <>
            <CardHeader
              avatar={
                <Tooltip title={t(`subStatus${subscription.data.Info.Status}`)}>
                  <Avatar className={classes.subAvatar}>
                    {getLetterFromStatus(subscription.data.Info.Status)}
                  </Avatar>
                </Tooltip>
              }
              title={t(subscription.data.Plan.Title)}
              subheader={getSubheader(
                subscription.data.Info,
                subscription.data.Estimate
              )}
              action={
                <>
                  {!isCancellable(subscription.data.Info) ? (
                    <Tooltip
                      title={
                        hasPaymentMethod ? "" : t('warnAddPaymentMethod')
                      }
                    >
                      <div className={classes.cardPrice}>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => reactivateSubscriptionHandler()}
                          disabled={!hasPaymentMethod || !subscription.data.Plan.IsActive}
                        >
                          {t("subscriptionReactivate")}
                        </Button>
                      </div>
                    </Tooltip>
                  ) : null}
                  {subscription.data.Estimate !== null && subscription.data.Estimate.InvoiceEstimate !== null ? (
                    <Tooltip
                      interactive
                      title={
                        <PriceDetails
                          invoiceEstimate={
                            subscription.data.Estimate.InvoiceEstimate
                          }
                          darkBg
                        />
                      }
                    >
                      <div className={classes.cardPrice}>
                        &euro;{" "}
                        {
                          subscription.data.Estimate.InvoiceEstimate.Total
                            .Amount
                        }
                      </div>
                    </Tooltip>
                  ) : null}
                </>
              }
            />
            <CardActions className={classes.cardActions}>
              <Tooltip title={t("info")}>
                <IconButton
                  onClick={ev => handleChange(ev, 0)}
                  className={clsx(
                    section === 0 ? classes.cardSelectedSection : ""
                  )}
                >
                  <FontAwesomeIcon icon="info-circle"/>
                </IconButton>
              </Tooltip>
              <Tooltip title={t("coupons")}>
                <IconButton
                  onClick={ev => handleChange(ev, 1)}
                  className={clsx(
                    section === 1 ? classes.cardSelectedSection : ""
                  )}
                >
                  <FontAwesomeIcon icon="tag"/>
                </IconButton>
              </Tooltip>

              <div className={classes.actionMore}>
                <Button variant="contained" color="primary" onClick={() => goToDashboard()} disabled={!canGoToDashboard(subscription.data.Info)}>
                    {t('dashboard')}
                </Button>
              </div>

              <Tooltip
                title={canChangePlan(subscription.data.Info) ? "" : t('warnAddPaymentMethod')}
              >
                <div className={classes.actionMore}>
                  <Button
                    color="primary"
                    onClick={changePlanHandler}
                    disabled={!canChangePlan(subscription.data.Info)}
                  >
                    {t('changePlan')}
                  </Button>
                </div>
              </Tooltip>

              <IconButton
                // className={clsx({
                //   [classes.actionMore]: isOwner
                // })}
                onClick={subMoreClickHandler}
              >
                <MoreVertIcon/>
              </IconButton>
            </CardActions>
            {section >= 0 ? (
              <CardContent>
                <TabPanel value={section} index={0}>
                  <ul>
                    {features.map((f, index) => (
                      <Typography component="li" key={index}>
                        {t(f)}
                      </Typography>
                    ))}
                  </ul>
                </TabPanel>
                <TabPanel value={section} index={1}>
                  {subscription.data.Coupon === null ? (
                    <Typography variant="h6" align="center">
                      {t("noCouponsApplied")}
                    </Typography>
                  ) : (
                    <Typography component="div" align="center">
                      <CouponDescription coupon={subscription.data.Coupon}/>
                    </Typography>
                  )}

                  <Divider style={{marginTop: 8, marginBottom: 8}}/>
                  <div style={{textAlign: "center"}}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={openCouponDialogHandler}
                    >
                      {t("applyCoupon")}
                    </Button>
                  </div>
                </TabPanel>
              </CardContent>
            ) : null}
            <Menu
              id="sub-more-menu"
              anchorEl={subMoreAnchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center"
              }}
              keepMounted
              open={open}
              onClose={subMoreCloseHandler}
              PaperProps={{
                style: {
                  maxHeight: ITEM_HEIGHT * 4.5
                  // width: 200,
                }
              }}
            >
              <MenuItem
                onClick={cancelSubscriptionHandler}
                disabled={!isCancellable(subscription.data.Info)}
              >
                <ListItemIcon>
                  <CancelIcon/>
                </ListItemIcon>
                <ListItemText primary={t("cancelSubscription")}/>
              </MenuItem>
            </Menu>
          </>
        )}
      </Card>

      <ConfirmDialog
        onClose={subCancelConfirmHandler}
        open={subCancelConfirmOpen}
        title={t("pleaseVerify")}
        content={
          <>
            <Typography variant="body1" paragraph>
              {t("confirmCancelSubscriptionParagraph1")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("confirmCancelSubscriptionParagraph2")}
            </Typography>
          </>
        }
        paragraph={false}
      />
      {acdOpen ? (
        <AddCouponDialog onClose={acdCloseHandler} open={acdOpen}/>
      ) : null}
      {redOpen ? (
        <ReactivateEstimateDialog onClose={redCloseHandler} open={redOpen}/>
      ) : null}
      {updateOpen ? (
        <ChangePlanDialog onClose={updateCloseHandler} open={updateOpen} />
      ) : null}
    </>
  );
};

const mapStateToProps = state => ({
  access_token: state.oidc.user.access_token,
  user: state.oidc.user,
  subscription: state.subscription,
  hasPaymentMethod: state.paymentMethods.list.data.length > 0
});

const mapDispatchToProps = dispatch => ({
  onFetchSubInfo: () => dispatch(subscriptionActions.fetchInfo()),
  onShowNotification: nt => dispatch(notificationActions.enqueueSnackbar(nt)),
  onFetchInvoices: () => dispatch(invoicesActions.fetch())
});

const composed = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SubscriptionStatus);

export default composed;
